/**
 * The comment layer's reads and writes.
 *
 * These sit here rather than in `src/api/queries.ts` because this directory owns
 * the comment layer, and the convention that file establishes is preserved
 * rather than forked: **the key is under the document prefix.**
 * `['document', nodeId, 'comments']` means every write that already invalidates
 * `['document', nodeId]` — a save, a publish, a restore — refreshes the margin
 * too, without any of those mutations having to learn that comments exist. That
 * matters more than it looks: an anchor's state is a function of the body, so a
 * write that changes the body can orphan a comment (§8.1), and a margin that
 * kept rendering across a restore would be showing objections against text that
 * came back.
 *
 * The mutations invalidate by prefix for the same reason. Applying a suggestion
 * (§8.2) writes `document.content`, snapshots a version, resolves the thread,
 * and sweeps every other anchor on the document — four caches, one prefix, and
 * search on top because the index moved with the body (§9.3).
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client.ts';
import type {
  ApplySuggestionResponse,
  CommentListResponse,
  CommentMutationResponse,
  CreateCommentInput,
} from './types.ts';

export const commentKeys = {
  /** Under `['document', nodeId, …]` deliberately — see the note above. */
  list: (nodeId: string, version?: string) =>
    ['document', nodeId, 'comments', version ?? 'current'] as const,
  all: (nodeId: string) => ['document', nodeId, 'comments'] as const,
};

export function commentsQuery(nodeId: string, version?: string) {
  return queryOptions({
    queryKey: commentKeys.list(nodeId, version),
    queryFn: ({ signal }) =>
      api.get<CommentListResponse>('/api/comments', { documentId: nodeId, version }, signal),
    enabled: nodeId !== '',
  });
}

export function useComments(nodeId: string, options: { version?: string; enabled?: boolean } = {}) {
  return useQuery({
    ...commentsQuery(nodeId, options.version),
    enabled: nodeId !== '' && (options.enabled ?? true),
  });
}

/**
 * A comment, a reply, or a suggestion — one route, distinguished by what is
 * sent, exactly as the API is.
 *
 * Only the comment list is invalidated. Writing a comment does not touch the
 * document, and invalidating the prefix would refetch the body and the version
 * list to learn nothing.
 */
export function useCreateComment(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateCommentInput, 'documentId'>) =>
      api.post<CommentMutationResponse>('/api/comments', { ...input, documentId: nodeId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.all(nodeId) });
    },
  });
}

export function useResolveThread(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.post<CommentMutationResponse>(`/api/comments/${commentId}/resolve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.all(nodeId) });
    },
  });
}

/**
 * Withdrawal — soft, and by the author alone.
 *
 * Named for what it is rather than for the verb on the wire: §8.1 retains the
 * row, and a button labelled "delete" beside a record that survives deletion is
 * a promise the database will not keep.
 */
export function useWithdrawComment(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete<CommentMutationResponse>(`/api/comments/${commentId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.all(nodeId) });
    },
  });
}

export function useApplySuggestion(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.post<ApplySuggestionResponse>(`/api/comments/${commentId}/apply`),
    onSuccess: () => {
      // The whole document prefix: the body changed, a version was forced
      // (§7.5 — somebody else's words are not coalesced into the applier's open
      // burst), the diff moved, and the sweep may have orphaned other threads.
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });
}
